import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {HTMLElement | null} */
  #secondaryNavEl = null;

  /** @type {(() => void) | null} */
  #teardownBelowShelf = null;

  /** @type {number | null} */
  #belowShelfScrollRaf = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#secondaryNavEl = this.querySelector('[data-drawer-secondary-nav]');
    if (this.#secondaryNavEl && this.refs.menuDrawer) {
      this.refs.menuDrawer.addEventListener('toggle', this.#onMenuDrawerNestedToggle, true);
      this.#secondaryNavEl.addEventListener('transitionend', this.#onSecondaryNavTransitionEnd);
      this.#syncSecondaryNavA11y();
    }
    if (this.hasAttribute('data-accordion-single-open') && this.refs.menuDrawer) {
      this.refs.menuDrawer.addEventListener('toggle', this.#onL1AccordionSingleOpen, true);
    }
    if (this.hasAttribute('data-drawer-below-shelf')) {
      this.#initBelowHeaderShelf();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.refs.menuDrawer?.removeEventListener('toggle', this.#onMenuDrawerNestedToggle, true);
    this.refs.menuDrawer?.removeEventListener('toggle', this.#onL1AccordionSingleOpen, true);
    this.#secondaryNavEl?.removeEventListener('transitionend', this.#onSecondaryNavTransitionEnd);
    this.#teardownBelowShelf?.();
    this.#teardownBelowShelf = null;
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  /**
   * The drawer is visible when `.menu-open` is on the main `<details>`.
   * Relying on the `open` attribute alone is wrong: `#close` removes `menu-open` first
   * and only clears `open` in `reset()` after the close transition, so a gap of one
   * `hasAttribute('open')` + native summary toggle can block a quick re-open.
   */
  get isOpen() {
    return this.refs.details.classList.contains('menu-open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer (hamburger summary). Nested `<details>` may use
   * `on:toggle="/toggle"`; ignore those so only the main `refs.details` is driven.
   * `preventDefault` on the click keeps the browser from toggling the `open` attribute
   * in the same tick as this handler so close animation and re-open are consistent.
   */
  toggle(/** @type {Event} */ event) {
    if (event instanceof Event) {
      if (event.type === 'toggle' && event.target !== this.refs.details) {
        return;
      }
      if (event.type === 'click') {
        event.preventDefault();
      }
    }
    return this.isOpen ? this.close() : this.open(undefined, event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string | Event} [target] - e.g. `'submenu'` from `on:click="header-drawer/open/submenu"`, or the click event when only one arg is passed
   * @param {Event} [event]
   */
  open(target, event) {
    let resolvedTarget = target;
    let resolvedEvent = event;
    if (target instanceof Event) {
      resolvedEvent = target;
      resolvedTarget = undefined;
    }

    const details = this.#getDetailsElement(resolvedEvent);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Summary clicks also trigger the native <details> toggle. Without preventDefault, the browser
    // can re-toggle closed in the same gesture right after we set `open` (e.g. slide-in L1 rows).
    if (resolvedEvent && 'preventDefault' in resolvedEvent) {
      const re = /** @type {{ type?: string; target?: EventTarget | null; preventDefault(): void }} */ (
        resolvedEvent
      );
      if (re.type === 'click' && re.target && re.target instanceof Element) {
        const clickedSummary = re.target.closest('summary');
        if (clickedSummary && details === /** @type {Element} */(clickedSummary).parentElement) {
          re.preventDefault();
        }
      }
    }

    if (!details.hasAttribute('open')) {
      details.setAttribute('open', '');
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    if (this.hasAttribute('data-drawer-below-shelf')) {
      this.#updateBelowHeaderShelf();
    }
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (resolvedTarget) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      this.#syncSecondaryNavA11y();

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    this.#syncSecondaryNavA11y();

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        this.#syncSecondaryNavA11y();
        if (details === this.refs.details) {
          removeTrapFocus();
          const scope = this.refs.menuDrawer ?? this;
          for (const d of scope.querySelectorAll('details[open]')) {
            reset(d);
          }
          this.dispatchEvent(
            new CustomEvent('menu-drawer:reset-accordion', { bubbles: true, cancelable: false }),
          );
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * When a top-level accordion (accordion-custom) opens, close others in the same L1 list.
   * @param {Event} event
   */
  #onL1AccordionSingleOpen = (event) => {
    if (!(this.refs.menuDrawer instanceof HTMLElement)) return;
    const t = event.target;
    if (!(t instanceof HTMLDetailsElement) || !t.open) return;
    if (!t.closest('accordion-custom')) return;
    if (t.id?.startsWith('drawer-')) return;
    if (t.closest('#drawer-localization, drawer-localization-component')) return;

    const li = t.closest('ul.menu-drawer__menu > li');
    if (!li) return;
    const ul = li.parentElement;
    if (!ul) return;
    for (const sibling of ul.children) {
      if (sibling === li) continue;
      if (!(sibling instanceof HTMLElement)) continue;
      for (const ac of sibling.querySelectorAll('accordion-custom')) {
        const d = ac.querySelector('details[open]');
        if (d && d !== t) d.removeAttribute('open');
      }
    }
  };

  /**
   * Nested <details> inside the slide-out panel (submenus, mega accordion).
   * Runs after the open attribute updates so #shouldSuppressSecondaryNav is accurate.
   */
  #onMenuDrawerNestedToggle = () => {
    queueMicrotask(() => this.#syncSecondaryNavA11y());
  };

  /**
   * True when a slide-out submenu / mega panel is actually shown (not just `open` in the DOM).
   * Nested accordions can set `details.open` while inside a *closed* parent slide `<details>`;
   * those must be ignored or the bottom bar stays `--suppressed` on L1.
   *
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement} root - `.menu-drawer`
   * @returns {boolean}
   */
  #isDrawerDetailsVisible(details, root) {
    let el = details.parentElement;
    while (el && el !== root) {
      if (el instanceof HTMLDetailsElement && !el.open) return false;
      el = el.parentElement;
    }
    return true;
  }

  /**
   * Drives `.drawer-secondary-nav--suppressed` (scoped CSS cannot use `.menu-drawer:has(...)`).
   * @returns {boolean}
   */
  #shouldSuppressSecondaryNav() {
    const root = this.refs.menuDrawer;
    if (!root) return false;
    if (root.querySelector('.menu-drawer__submenu.menu-open')) return true;

    for (const d of root.querySelectorAll('details[open]')) {
      if (!(d instanceof HTMLDetailsElement) || !this.#isDrawerDetailsVisible(d, root)) continue;
      if (d.querySelector('.menu-drawer__submenu')) return true;
      // Slide drill-down panels only (`Details-menu-drawer-menu-item-*`). Accordion rows use
      // `Details-menu-drawer-{handle}` and must not suppress the secondary bar.
      if (d.id.startsWith('Details-menu-drawer-menu-item-')) return true;
    }
    return false;
  }

  /**
   * After slide-out motion, mark the bar hidden for assistive tech (CSS defers visibility).
   */
  #onSecondaryNavTransitionEnd = (event) => {
    const el = this.#secondaryNavEl;
    if (!(el instanceof HTMLElement)) return;
    const inner = el.querySelector('.drawer-secondary-nav__inner');
    if (event.target !== el && event.target !== inner) return;
    if (event.propertyName !== 'transform' && event.propertyName !== 'opacity') return;
    if (!this.#shouldSuppressSecondaryNav()) return;
    el.setAttribute('aria-hidden', 'true');
  };

  /**
   * Panel and dimmed backdrop start at the main header bar’s bottom edge in the viewport
   * (content above the bar is outside the overlay). Kept in sync on resize, scroll, and open.
   */
  #initBelowHeaderShelf = () => {
    const onResize = () => {
      this.#updateBelowHeaderShelf();
    };
    const scrollOptions = { capture: true, passive: true };
    const onScroll = () => {
      if (this.#belowShelfScrollRaf != null) return;
      this.#belowShelfScrollRaf = requestAnimationFrame(() => {
        this.#belowShelfScrollRaf = null;
        this.#updateBelowHeaderShelf();
      });
    };
    const onToggle = () => {
      this.#updateBelowHeaderShelf();
    };
    this.#teardownBelowShelf = () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, scrollOptions);
      this.refs.details?.removeEventListener('toggle', onToggle);
      if (this.#belowShelfScrollRaf != null) {
        cancelAnimationFrame(this.#belowShelfScrollRaf);
        this.#belowShelfScrollRaf = null;
      }
      this.#teardownBelowHeaderShelf();
    };
    this.refs.details.addEventListener('toggle', onToggle);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, scrollOptions);
    requestAnimationFrame(() => {
      this.#updateBelowHeaderShelf();
    });
  };

  #teardownBelowHeaderShelf = () => {
    document.body.style.removeProperty('--menu-drawer-below-shelf-top');
  };

  #updateBelowHeaderShelf = () => {
    const header = document.querySelector('#header-component');
    if (!(header instanceof HTMLElement)) {
      this.#teardownBelowHeaderShelf();
      return;
    }
    const b = header.getBoundingClientRect().bottom;
    // Slight overlap avoids a 1–2px hairline between shelf and overlay (subpixel vs. rounded px).
    const topPx = Math.max(0, Math.round(b) - 2);
    document.body.style.setProperty('--menu-drawer-below-shelf-top', `${topPx}px`);
  };

  /**
   * Keeps bottom drawer secondary bar aligned with submenu state for AT and keyboard.
   * `inert` applies immediately on hide; `aria-hidden` follows the CSS slide-out transition.
   */
  #syncSecondaryNavA11y() {
    const el = this.#secondaryNavEl;
    if (!(el instanceof HTMLElement)) return;

    const suppress = this.#shouldSuppressSecondaryNav();

    if (!suppress) {
      el.classList.remove('drawer-secondary-nav--suppressed');
      el.inert = false;
      el.removeAttribute('aria-hidden');
      return;
    }

    el.classList.add('drawer-secondary-nav--suppressed');
    el.inert = true;
    el.removeAttribute('aria-hidden');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.#shouldSuppressSecondaryNav()) return;
        const style = getComputedStyle(el);
        const visuallyHidden =
          style.visibility === 'hidden' || parseFloat(style.opacity) < 0.01;
        if (visuallyHidden) el.setAttribute('aria-hidden', 'true');
      });
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
